import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, TriviaQuestion } from '../store/gameStore';

const TriviaModal: React.FC = () => {
  const { currentTrivia, answerTrivia } = useGameStore();
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  
  if (!currentTrivia) return null;
  
  const handleAnswer = (index: number) => {
    setSelectedAnswer(index);
    const correct = index === currentTrivia.correctAnswer;
    setIsCorrect(correct);
    setShowResult(true);
    
    setTimeout(() => {
      answerTrivia(correct);
      setSelectedAnswer(null);
      setShowResult(false);
    }, 2000);
  };
  
  return (
    <motion.div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-3xl p-8 max-w-xl w-full shadow-2xl border border-purple-500/30"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
            <span className="text-2xl">❓</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">TRIVIA TIME!</h2>
            <p className="text-purple-300">Answer correctly to roll again</p>
          </div>
        </div>
        
        <div className="bg-black/30 rounded-2xl p-6 mb-6">
          <p className="text-xl text-white font-medium">{currentTrivia.question}</p>
        </div>
        
        <div className="grid gap-3">
          {currentTrivia.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrectAnswer = index === currentTrivia.correctAnswer;
            
            return (
              <motion.button
                key={index}
                onClick={() => !showResult && handleAnswer(index)}
                disabled={showResult}
                className={`
                  p-4 rounded-xl text-left font-medium transition-all
                  ${showResult && isCorrectAnswer
                    ? 'bg-green-500 text-white'
                    : showResult && isSelected && !isCorrect
                    ? 'bg-red-500 text-white'
                    : isSelected
                    ? 'bg-purple-600 text-white'
                    : 'bg-black/30 text-gray-300 hover:bg-purple-600/50 hover:text-white'
                  }
                `}
                whileHover={!showResult ? { scale: 1.02 } : {}}
                whileTap={!showResult ? { scale: 0.98 } : {}}
              >
                <span className="text-purple-400 mr-2">{String.fromCharCode(65 + index)}.</span>
                {option}
              </motion.button>
            );
          })}
        </div>
        
        <AnimatePresence>
          {showResult && (
            <motion.div
              className={`mt-6 p-4 rounded-xl text-center font-bold text-lg ${
                isCorrect ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {isCorrect ? '✅ Correct! Roll again!' : '❌ Wrong! Move backward!'}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default TriviaModal;
